import { Injectable } from '@angular/core';
import { ConfirmationModalService } from '@epicuro-next/ataraxia/modal';
import { MessageType } from '@epicuro-next/ataraxia/snack-bar';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { IBulkDeleteErrorResponse } from '@epicuro-next/data/engagement/engagement-models';
import { List } from '@epicuro-next/models/engagement';
import {
  APP_PRIVATE_PATHS,
  PRODUCT_LIST_SUBROUTES,
} from '@epicuro-next/navigation/constants';
import { actions as feedActions } from '@epicuro-next/state/news-feed-state';
import { go } from '@epicuro-next/state/router-store';
import {
  loadLookBooks,
  resetLookBooks,
} from '@epicuro-next/state/shop-the-look-state';
import { showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { arrayIsEmpty, isNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import * as actions from '../../actions';
import { selectAllProductLists } from '../../selectors';
import { getDeleteSuccessMessageByAmount } from '../../utils';

export const CONFIRM_DELETION_WITH_LOOKBOOK_RELATION_MESSAGE = (
  lookBooks: string[],
) =>
  $localize`:@@productListBulkDeletionConfirmationModalMessage:The lists you are trying to delete are being used in following lookbooks: ${lookBooks.join()}. If you confirm to delete these lists, the lists will be removed from relevant lookboks. If the relevant lookbooks currently have too few looks, whole lookbook will be deleted. Do you still wish to continue?`;
export const CONFIRM_DELETION_WITH_LOOKBOOK_RELATION_TITLE = $localize`:@@productListDeletionConfirmationModalTitle:Deleting list(s) with relation to lookbook`;
export const DELETE_LISTS_FAILED_MESSAGE = $localize`:@@BulkDeleteProductListsFailMessage:Failed to delete product list(s).`;

@Injectable()
export class BulkDeleteEffects {
  public bulkDeleteProductLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.bulkDeleteLists),
      switchMap((action) =>
        this.productListApi.bulkDelete({ ids: action.ids }).pipe(
          map(
            (response: {
              failedDeletingProductLists: IBulkDeleteErrorResponse[];
            }) => {
              if (
                isNullish(response) ||
                arrayIsEmpty(response.failedDeletingProductLists)
              ) {
                return actions.bulkDeleteListsSuccess({ ids: action.ids });
              }

              return actions.confirmBulkDeleteListsWithLookBook({
                ids: action.ids,
                failedDeletingProductLists: response.failedDeletingProductLists,
              });
            },
          ),
          catchError(() => {
            return of(actions.bulkDeleteListsFail());
          }),
        ),
      ),
    ),
  );

  public confirmBulkDeleteListsWithLookBook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.confirmBulkDeleteListsWithLookBook),
      switchMap((action) => {
        const lookBookNames: string[] = [
          ...new Set(
            action.failedDeletingProductLists.flatMap((item) => {
              if (!item.metadata?.length) {
                return [];
              }

              return item.metadata.map((metadata) => metadata.title);
            }),
          ),
        ];

        const deletedIds = action.ids.filter(
          (id) =>
            !action.failedDeletingProductLists.find(
              (item) => item.entityId === id,
            ),
        );

        return this.confirmationModalService
          .open({
            message:
              CONFIRM_DELETION_WITH_LOOKBOOK_RELATION_MESSAGE(lookBookNames),
            title: CONFIRM_DELETION_WITH_LOOKBOOK_RELATION_TITLE,
          })
          .pipe(
            switchMap(() =>
              this.productListApi.bulkDelete({
                ids: action.failedDeletingProductLists.map(
                  (item) => item.entityId,
                ),
                deleteFromLookBook: true,
              }),
            ),
            switchMap(() => [
              actions.bulkDeleteListsSuccess({ ids: action.ids }),
              resetLookBooks(),
              loadLookBooks(),
            ]),
            catchError(() =>
              of(actions.bulkDeleteListsSuccess({ ids: deletedIds })),
            ),
          );
      }),
    ),
  );

  public deleteProductListSuccessMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.bulkDeleteListsSuccess),
      map(({ ids }) => {
        return showSnackBarMessage({
          message: getDeleteSuccessMessageByAmount(ids.length),
          messageType: MessageType.SUCCESS,
        });
      }),
    ),
  );

  public deleteProductListSuccessDependencies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.bulkDeleteListsSuccess),
      withLatestFrom(this.store.pipe(select(selectAllProductLists))),
      map(([{ ids }, lists]) => {
        return lists.filter(
          (list) => ids.includes(list.id) && !!list.newsfeedId,
        );
      }),
      filter((lists) => !!lists.length),
      map((lists) => {
        return feedActions.deleteArticlesBulk({
          ids: lists.map((item) => item.newsfeedId as string),
        });
      }),
    ),
  );

  public bulkDeleteListsSuccessFinalize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.bulkDeleteListsSuccess),
      map(({ ids }) => {
        return actions.bulkDeleteListsSuccessFinalize({ ids });
      }),
    ),
  );

  public redirectOnBulkDeleteProductListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.bulkDeleteListsSuccessFinalize),
      withLatestFrom(this.store.pipe(select(selectAllProductLists))),
      map(([_, lists]: [Action, List[]]) => {
        const first = lists && lists.length ? lists[0] : null;

        if (!first) {
          return go({
            commands: [
              APP_PRIVATE_PATHS.browse.path,
              APP_PRIVATE_PATHS.search.path,
            ],
          });
        }

        return go({
          commands: [
            APP_PRIVATE_PATHS.browse.path,
            APP_PRIVATE_PATHS.productList.path,
            first.id,
            PRODUCT_LIST_SUBROUTES.details,
          ],
        });
      }),
    ),
  );

  public deleteProductListFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.bulkDeleteListsFail),
      map(() => {
        return showSnackBarMessage({
          message: DELETE_LISTS_FAILED_MESSAGE,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private productListApi: ProductListApiService,
    private confirmationModalService: ConfirmationModalService,
    private store: Store,
  ) {}
}
